import {
  AbstractType,
  ChangeDetectionStrategy,
  Component,
  InjectionToken,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Type,
  ViewEncapsulation,
} from '@angular/core';
import { chain, get, isFunction, merge } from 'lodash';
import {
  EntityId,
  IFieldPreferences,
  GRID_ROW_SELECTION,
  IModalDialogOptions,
  ISite,
} from '@shared/types';
import { UiActions, XprtDataActions } from '@store/actions';

import { lazyInject } from '@app/containers/base/decorators';
import { StateService } from '@app/shared/services';
import { XVII18nService } from '@app/i18n/services';
import { Subscription, Observable, BehaviorSubject } from '@app/shared/rx';

import { BTN_CANCEL, BTN_OK } from '@app/text-content/button-templates';
import {
  getSiteSelectorButtonCaption,
  getSiteSelectorColumnsDefinition,
} from '@app/shared/utils';
import {
  defaultWidgetData,
  gridSelectModalOptions,
} from '../xux-edit-grid/xux-edit-grid-widget.settings';
import { first } from 'rxjs/operators';
import { I18n } from '@app/i18n/strings-keys';
import { WIDGETS_STATIC_TEXT_CONTENT } from '@app/i18n/constants/static-text-content.constants';

@Component({
  selector: 'xux-select-site-with-grid-modal',
  templateUrl: './xux-select-site-with-grid-modal-widget.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XuxSelectSiteWithGridModalComponent implements OnInit, OnDestroy {
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(XprtDataActions) public xprtDataActions: XprtDataActions;
  @lazyInject(XVII18nService) public i18nService: XVII18nService;

  @Input() public field: IFieldPreferences;

  private multiply: boolean = false;
  private subscriptions: Subscription[] = [];
  private disabled: boolean = false;
  public sitesDisplayCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public siteModalDescription$: BehaviorSubject<string>;
  public siteList$: BehaviorSubject<ISite[]> = new BehaviorSubject(null);

  public constructor(
    public uiActions: UiActions,
    public injector: Injector,
  ) {}

  public ngOnInit(): void {
    this.siteModalDescription$ = new BehaviorSubject<string>(
      this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
        count: 0,
        entities: this.i18nService.instant(
          I18n.EntityNames.siteSingularAndPlural,
        ),
      }),
    );
    this.multiply = get(this.field, 'widgetData.isMultiple', false);

    this.siteList$ = get(this.field, 'widgetData.dataSource');

    this.subscriptions.push(
      this.sitesDisplayCount$.subscribe((count: number): void => {
        const sitesSubTitle = this.i18nService.instant(
          I18n.Forms.GridMessages.ShowingEntities,
          {
            count,
            entities: this.i18nService.instant(
              I18n.EntityNames.siteSingularAndPlural,
            ),
          },
        );

        this.siteModalDescription$.next(sitesSubTitle);
      }),
    );

    this.handleDisable();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub?.unsubscribe());
  }

  public handleDisable(): void {
    const disabledObs: Observable<boolean> = get(this, 'field.disabledObs');

    if (disabledObs) {
      this.subscriptions.push(
        disabledObs.subscribe((isDisabled: boolean) => {
          this.disabled = isDisabled;
        }),
      );
    }
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public async selectClickHandler(): Promise<void> {
    if (this.disabled) {
      return;
    }
    await this.openSiteSelector();
  }

  public async openSiteSelector(): Promise<void> {
    const sitesList$: Observable<ISite[]> = this.siteList$.pipe(first());
    const sitesListPromise: ISite[] = await sitesList$.toPromise();
    const entityIdsForMetadataList: EntityId[] = chain(sitesListPromise)
      .flatMap('metadata')
      .map('entity_id')
      .value();
    const siteSelectorGridSettings: IModalDialogOptions = {
      title: this.i18nService.instant(I18n.Common.SiteSelector),
      subTitle: this.siteModalDescription$,
      modalClass: `modal-lg`,
      buttons: [
        {
          ...BTN_OK,
          caption: (selectedModalSites) =>
            getSiteSelectorButtonCaption({
              ...selectedModalSites,
              i18nService: this.i18nService,
            }),
          class: 'pull-right btn-primary',
        },
        BTN_CANCEL,
      ],
      schema: {
        properties: {
          items: { type: 'array' },
        },
      },
      value: {
        items: this.field.control.value,
      },
      formRules: {
        fields: {
          items: {
            ignoreAutoTitleWhenTranslate: true,
            widgetData: {
              displayCountObs: this.sitesDisplayCount$,
              valueField: 'id',
              returnFullData: false,
              treeViewMode: true,
              treeChildrenPropertyName: 'children',
              treeColumnTitle: 'hierarchy',
              defaultExpanded: false,
              searchPlaceholder: this.i18nService.instant(I18n.Common.Search),
              ignoreItemValueOptimization: true,
              useVisibleItemCountDescription: true,
              columns: this.i18nService.translateTextInColumns(
                getSiteSelectorColumnsDefinition(entityIdsForMetadataList),
              ),
              staticTextContent: {
                xuxGrid: WIDGETS_STATIC_TEXT_CONTENT.xuxGrid,
              },
              userGridOptions: {
                rowSelection:
                  this.multiply === false
                    ? GRID_ROW_SELECTION.SINGLE
                    : GRID_ROW_SELECTION.MULTIPLE,
                overlayNoRowsTemplate: this.i18nService.instant(
                  I18n.Common.NoRowsToShow,
                ),
                suppressRowClickSelection: true,
              },
            },
          },
        },
      },
    };

    const modalOptions: IModalDialogOptions = merge(
      {},
      gridSelectModalOptions(sitesList$, this.i18nService),
      defaultWidgetData,
      siteSelectorGridSettings,
    );

    const { confirmed, value } =
      await this.uiActions.formModalDialog(modalOptions);
    if (!confirmed) return;
    this.setValue(get(value, 'items'));
  }

  protected setValue(value): void {
    this.field.control.setValue(value);
    this.field.control.markAsDirty();
  }
}
